import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Search, RefreshCw, Trash2, ExternalLink, Download, Wand2, Sparkles,
  LayoutTemplate, Eye, Copy, Info, Check,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
// Hardcoded React-rendered designs (Template1–11) are intentionally not
// merged into this admin view — they're not part of the Wizard/Docmosis
// pipeline.  Marcus only manages real .docx templates here.

// Wizard URL — Resume Template Wizard on Railway.
const WIZARD_URL = "https://wizard-production-de29.up.railway.app";

type TemplateSource = "wizard" | "upload";

interface UnifiedRow {
  key: string;                     // unique render key
  source: TemplateSource;
  name: string;
  filename?: string;               // EXACT name to upload to Docmosis with
  category?: string;
  description?: string;
  createdAt?: string;              // ISO or display string
  isActive: boolean;
  // db-only fields (for actions)
  dbId?: string;
  storagePath?: string;
  fileUrl?: string | null;
}

interface TemplateRow {
  id: string;
  user_id: string;
  template_name: string;
  original_filename: string;
  template_file_path: string;
  template_file_url: string | null;
  field_mappings: unknown;
  style_metadata: { source?: string; wizard_starter?: string; published_at?: number } | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function AdminTemplates() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<"all" | "wizard" | "upload">("all");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>("");
  const [copiedFilename, setCopiedFilename] = useState<string | null>(null);

  function copyDocmosisName(filename: string) {
    navigator.clipboard.writeText(filename).then(
      () => {
        setCopiedFilename(filename);
        toast({ title: "Copied", description: `"${filename}" copied to clipboard` });
        setTimeout(() => setCopiedFilename(prev => prev === filename ? null : prev), 1500);
      },
      () => toast({ title: "Copy failed", variant: "destructive" })
    );
  }
  const [includeInactive, setIncludeInactive] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<UnifiedRow | null>(null);

  async function fetchTemplates() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("custom_resume_templates")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setTemplates(data || []);
    } catch (err: any) {
      toast({
        title: "Error loading templates",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchTemplates(); }, []);

  // Unified row model — only DB-backed templates appear here.  The legacy
  // hardcoded React designs aren't shown because they aren't part of the
  // Wizard/Docmosis pipeline.
  const allRows: UnifiedRow[] = useMemo(() => {
    return templates.map((t) => ({
      key: `db:${t.id}`,
      source: t.style_metadata?.source === "resume-template-wizard" ? "wizard" : "upload",
      name: t.template_name,
      filename: t.original_filename,
      createdAt: t.created_at,
      isActive: t.is_active,
      dbId: t.id,
      storagePath: t.template_file_path,
      fileUrl: t.template_file_url,
    }));
  }, [templates]);

  const filtered = useMemo(() => {
    return allRows.filter((r) => {
      if (!includeInactive && !r.isActive) return false;
      if (filter !== "all" && r.source !== filter) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        if (!r.name.toLowerCase().includes(q)
            && !(r.filename || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allRows, searchTerm, filter, includeInactive]);

  async function toggleActive(r: UnifiedRow) {
    if (r.source === "builtin" || !r.dbId) return;
    try {
      const { error } = await supabase
        .from("custom_resume_templates")
        .update({ is_active: !r.isActive })
        .eq("id", r.dbId);
      if (error) throw error;
      setTemplates((prev) => prev.map((x) =>
        x.id === r.dbId ? { ...x, is_active: !r.isActive } : x));
      toast({ title: !r.isActive ? "Template published" : "Template hidden" });
    } catch (err: any) {
      toast({ title: "Update failed", description: err.message, variant: "destructive" });
    }
  }

  async function hardDelete(r: UnifiedRow) {
    if (r.source === "builtin" || !r.dbId) return;
    try {
      if (r.storagePath) {
        await supabase.storage.from("word-documents").remove([r.storagePath]);
      }
      const { error } = await supabase
        .from("custom_resume_templates")
        .delete()
        .eq("id", r.dbId);
      if (error) throw error;
      setTemplates((prev) => prev.filter((x) => x.id !== r.dbId));
      toast({ title: `Deleted "${r.name}"` });
    } catch (err: any) {
      toast({ title: "Delete failed", description: err.message, variant: "destructive" });
    } finally {
      setDeleteTarget(null);
    }
  }

  const wizardCount = templates.filter(
    (t) => t.style_metadata?.source === "resume-template-wizard"
  ).length;
  const uploadsCount = templates.filter(
    (t) => t.style_metadata?.source !== "resume-template-wizard"
  ).length;
  const activeCount = templates.filter((t) => t.is_active).length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Templates</h1>
          <p className="text-muted-foreground mt-1">
            Resume templates available in the user-facing design picker.
            Build new ones in the Wizard.
          </p>
        </div>
        <Button asChild size="lg">
          <a href={WIZARD_URL} target="_blank" rel="noreferrer">
            <Wand2 className="mr-2 h-4 w-4" />
            Open Template Wizard
            <ExternalLink className="ml-2 h-3 w-3" />
          </a>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total templates</CardDescription>
            <CardTitle className="text-3xl">{templates.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active (visible to users)</CardDescription>
            <CardTitle className="text-3xl">{activeCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Built in Wizard</CardDescription>
            <CardTitle className="text-3xl">{wizardCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card className="border-amber-300 bg-amber-50/50">
        <CardContent className="py-3 flex items-start gap-3">
          <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Uploading to Docmosis Tornado:</strong> for each published template
            below, download the .docx (⬇ Download icon) and upload it to your Docmosis
            account using <strong>the exact filename shown in the "Docmosis name" column</strong>.
            That filename is what the TailorWiz edge function will look up at render time.
            The copy button next to each name puts it in your clipboard.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or filename"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              <Button variant={filter === "all" ? "default" : "outline"} size="sm"
                onClick={() => setFilter("all")}>All</Button>
              <Button variant={filter === "wizard" ? "default" : "outline"} size="sm"
                onClick={() => setFilter("wizard")}>
                <Sparkles className="mr-1 h-3 w-3" />Wizard
              </Button>
              <Button variant={filter === "upload" ? "default" : "outline"} size="sm"
                onClick={() => setFilter("upload")}>Uploads</Button>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} id="incl-inactive" />
              <label htmlFor="incl-inactive" className="text-sm text-muted-foreground">
                Show inactive
              </label>
            </div>
            <Button variant="outline" size="sm" onClick={fetchTemplates} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="min-w-[200px]">
                  <div className="flex items-center gap-1.5">
                    <span>Docmosis name</span>
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </div>
                </TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-center">Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <RefreshCw className="inline-block mr-2 h-4 w-4 animate-spin" />
                    Loading templates…
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    <LayoutTemplate className="inline-block mr-2 h-4 w-4" />
                    No templates yet. Open the Wizard above to build your first one.
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.map((r) => (
                <TableRow key={r.key}>
                  <TableCell>
                    <div className="font-medium">{r.name}</div>
                  </TableCell>
                  <TableCell>
                    {r.filename ? (
                      <div className="flex items-center gap-1.5">
                        <code className="px-2 py-1 bg-muted rounded text-xs font-mono break-all">
                          {r.filename}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 flex-shrink-0"
                          onClick={() => copyDocmosisName(r.filename!)}
                          title="Copy this exact filename for Docmosis upload"
                        >
                          {copiedFilename === r.filename ? (
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.source === "wizard" ? (
                      <Badge variant="default" className="gap-1">
                        <Sparkles className="h-3 w-3" /> Wizard
                      </Badge>
                    ) : (
                      <Badge variant="outline">Upload</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={r.isActive}
                      onCheckedChange={() => toggleActive(r)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {r.fileUrl && (
                        <Button variant="ghost" size="icon" title="Preview design"
                          onClick={() => { setPreviewUrl(r.fileUrl!); setPreviewName(r.name); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {r.fileUrl && (
                        <Button variant="ghost" size="icon" asChild title="Download .docx">
                          <a href={r.fileUrl} target="_blank" rel="noreferrer">
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" asChild title="Open in Wizard">
                        <a href={WIZARD_URL} target="_blank" rel="noreferrer">
                          <Wand2 className="h-4 w-4" />
                        </a>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(r)}
                        title="Delete permanently"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!previewUrl} onOpenChange={(o) => !o && setPreviewUrl(null)}>
        <DialogContent className="max-w-5xl w-[92vw] h-[88vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" /> {previewName || "Template preview"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-muted/30">
            {previewUrl && (
              <iframe
                title={previewName || "Template preview"}
                src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(previewUrl)}`}
                className="w-full h-full border-0"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> will be permanently removed
              from the user picker and the .docx file deleted from storage. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && hardDelete(deleteTarget)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
